import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import type {
  VisualEditorState,
  VisualEditorContextType,
  SectionData,
  SectionBoundsResponsePayload,
} from '../types/visualEditor';

// Initial state
const initialState: VisualEditorState = {
  selectedSectionIndex: null,
  hoveredSectionIndex: null,
  editingField: null,
  isInlineEditing: false,
  isDragging: false,
  dragSourceIndex: null,
  dragOverIndex: null,
  sectionBounds: new Map(),
  iframeReady: false,
  pendingUpdates: new Map(),
  lastSyncTimestamp: 0,
  isAddSectionModalOpen: false,
  addSectionAfterIndex: -1,
};

// Action types
type VisualEditorAction =
  | { type: 'SELECT_SECTION'; payload: number | null }
  | { type: 'HOVER_SECTION'; payload: number | null }
  | { type: 'START_INLINE_EDIT'; payload: { sectionIndex: number; fieldName: string } }
  | { type: 'STOP_INLINE_EDIT' }
  | { type: 'START_DRAG'; payload: number }
  | { type: 'UPDATE_DRAG_OVER'; payload: number | null }
  | { type: 'END_DRAG' }
  | { type: 'CANCEL_DRAG' }
  | { type: 'SET_SECTION_BOUNDS'; payload: SectionBoundsResponsePayload }
  | { type: 'SET_IFRAME_READY'; payload: boolean }
  | { type: 'OPEN_ADD_SECTION_MODAL'; payload: number }
  | { type: 'CLOSE_ADD_SECTION_MODAL' }
  | { type: 'UPDATE_SYNC_TIMESTAMP' };

// Reducer
function visualEditorReducer(
  state: VisualEditorState,
  action: VisualEditorAction
): VisualEditorState {
  switch (action.type) {
    case 'SELECT_SECTION':
      return {
        ...state,
        selectedSectionIndex: action.payload,
        // Clear inline editing when selecting a different section
        editingField: action.payload !== state.selectedSectionIndex ? null : state.editingField,
        isInlineEditing: action.payload !== state.selectedSectionIndex ? false : state.isInlineEditing,
      };

    case 'HOVER_SECTION':
      return {
        ...state,
        hoveredSectionIndex: action.payload,
      };

    case 'START_INLINE_EDIT':
      return {
        ...state,
        editingField: action.payload,
        isInlineEditing: true,
        selectedSectionIndex: action.payload.sectionIndex,
      };

    case 'STOP_INLINE_EDIT':
      return {
        ...state,
        editingField: null,
        isInlineEditing: false,
      };

    case 'START_DRAG':
      return {
        ...state,
        isDragging: true,
        dragSourceIndex: action.payload,
        // Clear inline editing when starting drag
        editingField: null,
        isInlineEditing: false,
      };

    case 'UPDATE_DRAG_OVER':
      return {
        ...state,
        dragOverIndex: action.payload,
      };

    case 'END_DRAG':
    case 'CANCEL_DRAG':
      return {
        ...state,
        isDragging: false,
        dragSourceIndex: null,
        dragOverIndex: null,
      };

    case 'SET_SECTION_BOUNDS': {
      const newBounds = new Map(state.sectionBounds);
      action.payload.sections.forEach((section) => {
        newBounds.set(section.index, section);
      });
      return {
        ...state,
        sectionBounds: newBounds,
      };
    }

    case 'SET_IFRAME_READY':
      return {
        ...state,
        iframeReady: action.payload,
      };

    case 'OPEN_ADD_SECTION_MODAL':
      return {
        ...state,
        isAddSectionModalOpen: true,
        addSectionAfterIndex: action.payload,
      };

    case 'CLOSE_ADD_SECTION_MODAL':
      return {
        ...state,
        isAddSectionModalOpen: false,
        addSectionAfterIndex: -1,
      };

    case 'UPDATE_SYNC_TIMESTAMP':
      return {
        ...state,
        lastSyncTimestamp: Date.now(),
      };

    default:
      return state;
  }
}

// Context
const VisualEditorContext = createContext<VisualEditorContextType | undefined>(undefined);

// Provider props
interface VisualEditorProviderProps {
  children: React.ReactNode;
  sections: SectionData[];
  onSectionsChange: (sections: SectionData[]) => void;
  onSectionBoundsRequest?: () => void;
}

// Provider component
export const VisualEditorProvider: React.FC<VisualEditorProviderProps> = ({
  children,
  sections,
  onSectionsChange,
  onSectionBoundsRequest,
}) => {
  const [state, dispatch] = useReducer(visualEditorReducer, initialState);

  // Selection actions
  const selectSection = useCallback((index: number | null) => {
    dispatch({ type: 'SELECT_SECTION', payload: index });
  }, []);

  const hoverSection = useCallback((index: number | null) => {
    dispatch({ type: 'HOVER_SECTION', payload: index });
  }, []);

  // Inline editing actions
  const startInlineEdit = useCallback((sectionIndex: number, fieldName: string) => {
    dispatch({ type: 'START_INLINE_EDIT', payload: { sectionIndex, fieldName } });
  }, []);

  const stopInlineEdit = useCallback(() => {
    dispatch({ type: 'STOP_INLINE_EDIT' });
  }, []);

  const updateFieldValue = useCallback(
    (sectionIndex: number, fieldName: string, value: string) => {
      const newSections = [...sections];
      if (newSections[sectionIndex]) {
        newSections[sectionIndex] = {
          ...newSections[sectionIndex],
          data: {
            ...newSections[sectionIndex].data,
            [fieldName]: value,
          },
        };
        onSectionsChange(newSections);
        dispatch({ type: 'UPDATE_SYNC_TIMESTAMP' });
      }
    },
    [sections, onSectionsChange]
  );

  // Drag-drop actions
  const startDrag = useCallback((index: number) => {
    dispatch({ type: 'START_DRAG', payload: index });
  }, []);

  const updateDragOver = useCallback((index: number | null) => {
    dispatch({ type: 'UPDATE_DRAG_OVER', payload: index });
  }, []);

  const endDrag = useCallback((toIndex?: number) => {
    dispatch({ type: 'END_DRAG' });
  }, []);

  const cancelDrag = useCallback(() => {
    dispatch({ type: 'CANCEL_DRAG' });
  }, []);

  // Section management actions
  const addSection = useCallback(
    (afterIndex: number, sectionType: string, variant?: string) => {
      const newSection: SectionData = {
        type: sectionType,
        variant: variant || 'default',
        data: {},
        order: afterIndex + 1,
      };

      const newSections = [...sections];
      newSections.splice(afterIndex + 1, 0, newSection);

      // Update order for all sections
      newSections.forEach((section, idx) => {
        section.order = idx;
      });

      onSectionsChange(newSections);
      dispatch({ type: 'CLOSE_ADD_SECTION_MODAL' });
      dispatch({ type: 'UPDATE_SYNC_TIMESTAMP' });

      // Select the newly added section
      dispatch({ type: 'SELECT_SECTION', payload: afterIndex + 1 });
    },
    [sections, onSectionsChange]
  );

  const deleteSection = useCallback(
    (index: number) => {
      const newSections = sections.filter((_, idx) => idx !== index);

      // Update order for remaining sections
      newSections.forEach((section, idx) => {
        section.order = idx;
      });

      onSectionsChange(newSections);

      // Clear selection if deleted section was selected
      if (state.selectedSectionIndex === index) {
        dispatch({ type: 'SELECT_SECTION', payload: null });
      } else if (state.selectedSectionIndex !== null && state.selectedSectionIndex > index) {
        // Adjust selection index if a section before it was deleted
        dispatch({ type: 'SELECT_SECTION', payload: state.selectedSectionIndex - 1 });
      }

      dispatch({ type: 'UPDATE_SYNC_TIMESTAMP' });
    },
    [sections, onSectionsChange, state.selectedSectionIndex]
  );

  const duplicateSection = useCallback(
    (index: number) => {
      const sectionToDuplicate = sections[index];
      if (!sectionToDuplicate) return;

      const newSection: SectionData = {
        ...JSON.parse(JSON.stringify(sectionToDuplicate)), // Deep clone
        order: index + 1,
      };

      const newSections = [...sections];
      newSections.splice(index + 1, 0, newSection);

      // Update order for all sections
      newSections.forEach((section, idx) => {
        section.order = idx;
      });

      onSectionsChange(newSections);
      dispatch({ type: 'UPDATE_SYNC_TIMESTAMP' });

      // Select the duplicated section
      dispatch({ type: 'SELECT_SECTION', payload: index + 1 });
    },
    [sections, onSectionsChange]
  );

  const moveSection = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;

      const newSections = [...sections];
      const [movedSection] = newSections.splice(fromIndex, 1);
      newSections.splice(toIndex, 0, movedSection);

      // Update order for all sections
      newSections.forEach((section, idx) => {
        section.order = idx;
      });

      onSectionsChange(newSections);
      dispatch({ type: 'UPDATE_SYNC_TIMESTAMP' });

      // Update selection to follow the moved section
      if (state.selectedSectionIndex === fromIndex) {
        dispatch({ type: 'SELECT_SECTION', payload: toIndex });
      }
    },
    [sections, onSectionsChange, state.selectedSectionIndex]
  );

  // Sync actions
  const refreshSectionBounds = useCallback(() => {
    onSectionBoundsRequest?.();
  }, [onSectionBoundsRequest]);

  const setIframeReady = useCallback((ready: boolean) => {
    dispatch({ type: 'SET_IFRAME_READY', payload: ready });
  }, []);

  // Add section modal actions
  const openAddSectionModal = useCallback((afterIndex: number) => {
    dispatch({ type: 'OPEN_ADD_SECTION_MODAL', payload: afterIndex });
  }, []);

  const closeAddSectionModal = useCallback(() => {
    dispatch({ type: 'CLOSE_ADD_SECTION_MODAL' });
  }, []);

  // Handle section bounds update from iframe
  const updateSectionBounds = useCallback((payload: SectionBoundsResponsePayload) => {
    dispatch({ type: 'SET_SECTION_BOUNDS', payload });
  }, []);

  // Memoize context value
  const contextValue = useMemo<VisualEditorContextType>(
    () => ({
      // State
      ...state,

      // Actions
      selectSection,
      hoverSection,
      startInlineEdit,
      stopInlineEdit,
      updateFieldValue,
      startDrag,
      updateDragOver,
      endDrag,
      cancelDrag,
      addSection,
      deleteSection,
      duplicateSection,
      moveSection,
      refreshSectionBounds,
      setIframeReady,
      updateSectionBounds,
      openAddSectionModal,
      closeAddSectionModal,
    }),
    [
      state,
      selectSection,
      hoverSection,
      startInlineEdit,
      stopInlineEdit,
      updateFieldValue,
      startDrag,
      updateDragOver,
      endDrag,
      cancelDrag,
      addSection,
      deleteSection,
      duplicateSection,
      moveSection,
      refreshSectionBounds,
      setIframeReady,
      updateSectionBounds,
      openAddSectionModal,
      closeAddSectionModal,
    ]
  );

  return (
    <VisualEditorContext.Provider value={contextValue}>
      {children}
    </VisualEditorContext.Provider>
  );
};

// Hook to use the context
export const useVisualEditor = (): VisualEditorContextType => {
  const context = useContext(VisualEditorContext);
  if (context === undefined) {
    throw new Error('useVisualEditor must be used within a VisualEditorProvider');
  }
  return context;
};

// Export the context for advanced usage
export { VisualEditorContext };
